import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * BackButton — one-click return to the previously visited page.
 *
 * Uses the browser history (navigate(-1)) so it lands on wherever the user
 * actually came from, not on the module home. When history is empty (deep
 * link, PWA cold start, hard refresh), falls back to `fallback` prop.
 *
 * Usage:
 *   <BackButton fallback="/producers" label="Producers" />
 *
 * Variants:
 *   - default: pill with border, ← icon and label
 *   - variant="link": inline link style (no border)
 */
export default function BackButton({
  fallback = "/",
  label,
  variant = "default",
  className = "",
  testid = "back-button",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();

  // A single-entry history stack means we opened this URL directly (deep
  // link / cold start), so `navigate(-1)` would leave the app entirely.
  const hasHistory =
    typeof window !== "undefined" &&
    window.history &&
    window.history.length > 1 &&
    // Also treat first location key ("default") as no in-app history.
    location.key !== "default";

  const onClick = () => {
    if (hasHistory) navigate(-1);
    else navigate(fallback);
  };

  const text = label || t("common.back") || "Retour";

  if (variant === "link") {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 text-sm text-[#E5276A] font-bold hover:underline ${className}`}
      >
        <ArrowLeft className="w-4 h-4" /> {text}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1F3A5F]/15 bg-white text-[#1F3A5F] text-sm font-bold hover:border-[#E5276A] hover:text-[#E5276A] transition-colors ${className}`}
    >
      <ArrowLeft className="w-4 h-4" /> {text}
    </button>
  );
}

// End of file — no additional imports needed.
